// Helper to format date for Excel
function formatDateForExcel(isoDate) {
    if (!isoDate)
        return '';
    try {
        const date = new Date(isoDate);
        // Excel-friendly format: MM/DD/YYYY HH:MM:SS
        return date.toLocaleString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
        });
    }
    catch {
        return '';
    }
}
// Helper to join array values
function joinArray(arr) {
    return arr?.join(', ') || '';
}
// Define all available lead export columns
export const LEAD_EXPORT_COLUMNS = [
    { id: 'firstName', label: 'First Name', getValue: (l) => l.FirstName || '' },
    { id: 'lastName', label: 'Last Name', getValue: (l) => l.LastName || '' },
    { id: 'fullName', label: 'Full Name', getValue: (l) => l.FullName || '' },
    { id: 'email', label: 'Email', getValue: (l) => l.Email || '' },
    { id: 'phone', label: 'Phone', getValue: (l) => l.Phone || l.GlobalContactPhone || '' },
    { id: 'title', label: 'Title', getValue: (l) => l.JobTitle || '' },
    { id: 'company', label: 'Company', getValue: (l) => l.Company || '' },
    { id: 'companyWebsite', label: 'Company Website', getValue: (l) => l.CompanyWebsite || '' },
    { id: 'domain', label: 'Domain', getValue: (l) => l.Domain || '' },
    { id: 'industry', label: 'Industry', getValue: (l) => l.Industry || l.CompanyIndustry || '' },
    { id: 'companySize', label: 'Company Size', getValue: (l) => l.CompanySize || l.CompanyStaffCountRange || '' },
    { id: 'country', label: 'Country', getValue: (l) => l.Country || '' },
    { id: 'enrichmentStatus', label: 'Enrichment Status', getValue: (l) => l.EnrichmentStatus || '' },
    { id: 'enrichedAt', label: 'Enriched At', getValue: (l) => formatDateForExcel(l.EnrichedAt) },
    { id: 'types', label: 'Web Types', getValue: (l) => joinArray(l.WebTypes) },
    { id: 'signalScore', label: 'Signal Score', getValue: (l) => l.SignalScore },
    { id: 'signalLevel', label: 'Signal Level', getValue: (l) => l.SignalLevel || '' },
    { id: 'interactionCount', label: 'Interaction Count', getValue: (l) => l.InteractionCount },
    { id: 'firstInteractionAt', label: 'First Interaction At', getValue: (l) => formatDateForExcel(l.FirstInteractionAt) },
    { id: 'lastInteractionAt', label: 'Last Interaction At', getValue: (l) => formatDateForExcel(l.LastInteractionAt) },
    { id: 'linkedinUrl', label: 'LinkedIn Profile', getValue: (l) => l.LinkedinProfileUrl || '' },
    { id: 'companyLinkedinUrl', label: 'Company LinkedIn', getValue: (l) => l.CompanyLinkedInUrl || '' },
    { id: 'isAudienceTarget', label: 'Is Audience Target', getValue: (l) => l.IsAudienceTarget ? 'Yes' : 'No' },
    { id: 'webinarAttended', label: 'Webinar Attended', getValue: (l) => l.WebinarAttended === true ? 'Yes' : l.WebinarAttended === false ? 'No' : '' },
    { id: 'attributionSource', label: 'Attribution Source', getValue: (l) => l.WebSourceAttributions?.[0]?.Source || '' },
];
// Define company export columns
export const COMPANY_EXPORT_COLUMNS = [
    { id: 'company', label: 'Company', getValue: (c) => c.CompanyName || '' },
    { id: 'companyWebsite', label: 'Company Website', getValue: (c) => c.CompanyWebsite || '' },
    { id: 'companyLinkedinUrl', label: 'Company LinkedIn', getValue: (c) => c.CompanyLinkedInUrl || '' },
    { id: 'industry', label: 'Industry', getValue: (c) => c.Industry || '' },
    { id: 'staffCountRange', label: 'Staff Count Range', getValue: (c) => c.StaffCountRange || '' },
    { id: 'leadCount', label: 'Lead Count', getValue: (c) => c.LeadCount },
    { id: 'signalScore', label: 'Signal Score', getValue: (c) => c.SignalScore },
    { id: 'lastConversionAt', label: 'Last Conversion At', getValue: (c) => formatDateForExcel(c.LastConversionAt) },
];
/**
 * Convert leads data to CSV format
 */
export function exportLeadsToCSV(leads, columns = LEAD_EXPORT_COLUMNS) {
    if (leads.length === 0)
        return '';
    // Create header row
    const headers = columns.map(col => col.label);
    // Create data rows
    const rows = leads.map(lead => columns.map(col => {
        const value = col.getValue(lead);
        // Escape values containing commas, quotes, or newlines
        if (value === null || value === undefined)
            return '';
        const stringValue = String(value);
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
            return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
    }));
    // Combine header and rows
    return [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
}
/**
 * Convert companies data to CSV format
 */
export function exportCompaniesToCSV(companies, columns = COMPANY_EXPORT_COLUMNS) {
    if (companies.length === 0)
        return '';
    // Create header row
    const headers = columns.map(col => col.label);
    // Create data rows
    const rows = companies.map(company => columns.map(col => {
        const value = col.getValue(company);
        // Escape values containing commas, quotes, or newlines
        if (value === null || value === undefined)
            return '';
        const stringValue = String(value);
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
            return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
    }));
    // Combine header and rows
    return [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
}
/**
 * Generate export filename with company, workspace, context, and detailed timestamp
 */
export function generateExportFilename(companyName, workspaceName, context, type) {
    const now = new Date();
    const timestamp = now.toISOString()
        .replace('T', ' ')
        .replace(/\..+/, '')
        .replace(/:/g, '-');
    const parts = [];
    if (companyName)
        parts.push(companyName);
    if (workspaceName)
        parts.push(workspaceName);
    parts.push(`${context} ${type === 'leads' ? 'Leads' : 'Companies'}`);
    parts.push(timestamp);
    return `${parts.join(' - ')}.csv`;
}
/**
 * Trigger browser download of CSV file
 */
export function downloadCSV(csvContent, filename) {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    // Create a temporary link element
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    // Prevent preact-iso router from intercepting this
    link.setAttribute('rel', 'noopener noreferrer');
    link.setAttribute('target', '_self');
    // Trigger download without adding to DOM to avoid routing issues
    const clickEvent = new MouseEvent('click', {
        view: window,
        bubbles: false,
        cancelable: true
    });
    link.dispatchEvent(clickEvent);
    // Clean up
    setTimeout(() => {
        URL.revokeObjectURL(url);
    }, 100);
}
/**
 * Export leads to Excel (CSV format)
 */
export function exportLeadsToExcel(leads, filename, columns = LEAD_EXPORT_COLUMNS) {
    const csv = exportLeadsToCSV(leads, columns);
    downloadCSV(csv, filename);
}
/**
 * Export companies to Excel (CSV format)
 */
export function exportCompaniesToExcel(companies, filename, columns = COMPANY_EXPORT_COLUMNS) {
    const csv = exportCompaniesToCSV(companies, columns);
    downloadCSV(csv, filename);
}
